"use client";

import type React from "react";
import { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useCart } from "../../hooks/shareContext";
import { useTranslation } from "react-i18next";
import { axiosInstance } from "../../utils/tokenSetCookie";
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
import type { SearchDropdownData, SearchResult } from "../../types/types";
import { toast } from "react-toastify";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import shareIcon from "../../assets/user_panel/icons/share.svg";
import { useNavigate, useLocation } from "react-router-dom";
import LoginRestriction from "../../utils/loginRestriction";
import { AxiosError } from "axios";

interface SearchDropdownProps {
  SearchList: (data: SearchResult[]) => void;
}

const SearchDropdown: React.FC<SearchDropdownProps> = ({ SearchList }) => {
  const {
    selectAnim,
    setSelectAnim,
    cartCount,
    incrementCartCount,
    decrementCartCount,
    setSystemNumber,
    setAnimationId,
    setActiveAnim,
    setActiveAnimIndex,
  } = useCart();

  const navigate = useNavigate();
  const location = useLocation();
  const [inputValue, setInputValue] = useState("");
  const { t } = useTranslation();
  const [searchList, setSearchList] = useState<SearchDropdownData[]>([]);
  const [autoCompleteKey, setAutoCompleteKey] = useState(0);

  const [typingTimeout, setTypingTimeout] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const autoCompleteRef = useRef<HTMLDivElement>(null);
  const [highlightedOption, setHighlightedOption] =
    useState<SearchDropdownData | null>(null);
  const [isRestrictionOpen, setIsRestrictionOpen] = useState(false);

  // Consolidated localStorage loading - only run once on component mount
  useEffect(() => {
    // Load selected animation from localStorage on component mount
    const savedAnimationId = localStorage.getItem("selectedAnimationId");
    const savedSystemId = localStorage.getItem("selectedSystemId");

    if (savedAnimationId && savedSystemId) {

      // Set all relevant state variables with the saved values
      setAnimationId(Number(savedAnimationId));
      setSystemNumber(Number(savedSystemId));
      setActiveAnimIndex(Number(savedAnimationId));
      setActiveAnim(Number(savedAnimationId));
    }
  }, []); // Empty dependency array ensures it runs only once

  useEffect(() => {
    if (inputValue.length === 0) {
      setSearchList([]);
    }
  }, [inputValue]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchText = event.target.value;
    setInputValue(searchText);

    if (typingTimeout) clearTimeout(typingTimeout); // Clear previous timeout

    if (searchText.length >= 2) {
      const timeout = setTimeout(() => {
        const fetchBannerData = async () => {
          const payload = {
            languageId: 50,
            searchText: searchText,
          };
          try {
            const response = await axiosInstance.post(
              API_BASE_URL + "animation/animation-search",
              payload
            );
            if (response.status >= 200 && response.status <= 300) {
              setSearchList(response.data.data);
              SearchList(response.data.data);
            } else {
            }
          } catch (error) {
            const errCode = error as AxiosError<{ status: number }>;
            if (errCode.response && errCode.response.status === 401) {
              setIsRestrictionOpen(true);
            } else {
              toast.error("Something went wrong", {
                position: "top-right",
                autoClose: 1000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
              });
            }
            throw error;
          }
        };
        fetchBannerData();
      }, 1000); // Delay API call by 1000ms

      setTypingTimeout(timeout);
    } else {
      SearchList([]); // Clear list when input is empty
    }
  };

  const handleClickLink = (id: number) => {
    if (selectAnim.some((aid) => aid === id)) {
      toast.warning(t("animation_already_in_cart"), {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } else {
      toast.success(t("animation_added_to_cart"), {
        position: "top-right",
        autoClose: 1000,
      });
      setSelectAnim((prevIds: number[]) => [...prevIds, id]);
      const newCount = cartCount + 1;
      incrementCartCount(newCount);
      setInputValue("");
      setAutoCompleteKey((prev) => prev + 1);
      setSearchList([]);
    }
  };

  const handleRemoveAnim = (rid: number) => {
    const updatedSelectAnim = selectAnim.filter((item) => item !== rid);
    setSelectAnim(updatedSelectAnim);
    const newCount = cartCount - 1;
    decrementCartCount(newCount);
    setInputValue("");
    setSearchList([]);
  };

  const AnimationDetails = (
    animationId: number,
    systemId: number,
    systemTagName?: string
  ) => {
    if (!animationId || !systemId) {
      toast.error("Invalid animation or system ID");
      return;
    }

    // Save selected animation to localStorage with stronger persistence
    try {

      // Set new values
      localStorage.setItem("selectedAnimationId", animationId.toString());
      localStorage.setItem("selectedSystemId", systemId.toString());
      localStorage.setItem("selectedSystemTagName", systemTagName as string);

    } catch (error) {
    }

    // Update all relevant state variables
    setAnimationId(animationId);
    setSystemNumber(systemId);
    setActiveAnimIndex(animationId);
    setActiveAnim(animationId);

    // Clear search input and results
    setInputValue("");
    setSearchList([]);
    setHighlightedOption(null);

    // Show success message
    toast.success(t("animation_selected"), {
      position: "top-right",
      autoClose: 1000,
    });

    // Navigate to home if not already there
    if (location.pathname !== "/home") {
      navigate("/home");
    } else if (location.pathname === "/home") {
      // If already on home page, force a component update without full page refresh
      // This is a workaround to ensure the slider updates with the new selection
      const event = new CustomEvent("animationSelected", {
        detail: { animationId, systemId },
      });
      window.dispatchEvent(event);
    }
  };
  useEffect(() => {
    const savedAnimationId = localStorage.getItem("selectedAnimationId");
    const savedSystemId = localStorage.getItem("selectedSystemId");

    if (savedAnimationId && savedSystemId) {
      setAnimationId(Number.parseInt(savedAnimationId));
      setSystemNumber(Number.parseInt(savedSystemId));
      setActiveAnimIndex(Number.parseInt(savedAnimationId));
      setActiveAnim(Number.parseInt(savedAnimationId));
    }
  }, []);

  return (
    <Box>
      <Autocomplete
        ref={autoCompleteRef}
        key={autoCompleteKey}
        className="search_anim"
        sx={{
          "& .MuiOutlinedInput-root": {
            borderColor: "#000000", // Border color for the entire input
            "& fieldset": {
              borderColor: "#1a76d2", // Default border color
            },
            "&:hover fieldset": {
              borderColor: "#1a76d2", // Border color on hover
            },
            "&.Mui-focused fieldset": {
              borderColor: "#1a76d2", // Border color when focused
            },
          },
        }}
        freeSolo
        options={searchList}
        getOptionLabel={(option) => (typeof option === "string" ? option : "")}
        isOptionEqualToValue={(option, value) =>
          typeof option !== "string" && typeof value !== "string"
            ? option.animationId === value.animationId
            : option === value
        }
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        filterOptions={(options) => options} // This ensures all options are always displayed
        noOptionsText="No results found" // Show this when the list is empty
        onHighlightChange={(_, option) => {
          if (option && typeof option !== "string") {
            setHighlightedOption(option); // Track the focused option
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault(); // Prevent form submission
            event.stopPropagation();

            if (highlightedOption) {
              AnimationDetails(
                highlightedOption.animationId,
                highlightedOption.systemNumber,
                highlightedOption.systemTagName
              );
              setHighlightedOption(null);
              setInputValue("");
              setSearchList([]);
            } else {
              toast.error("No animation selected.");
            }
          }
        }}
        renderInput={(params) => (
          <Box display="flex" alignItems="center">
            <TextField
              sx={{
                input: {
                  color: "#000000", // Text color inside the input
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#0000009a", // Dark black placeholder text
                  opacity: 1, // Ensure it is fully visible
                },
              }}
              className="field"
              {...params}
              placeholder={t("share_search_animations")}
              value={inputValue}
              onChange={handleSearch}
              variant="outlined"
              fullWidth
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{
                        color: "#020202f5", // Text color inside the input
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}
        renderOption={(props, option) => {
          if (searchList.length === 0) {
            return (
              <li
                {...props}
                key="no-results"
                style={{ textAlign: "center", padding: "10px" }}
              >
                {t("no_results_found")}
                {/* No results found */}
              </li>
            );
          }
          // Ensure option is of type SearchDropdownData
          if (typeof option === "string") return null;

          return (
            <Box
              sx={{
                borderBottom: "1px solid #cccccc52",
                width: "100%",
              }}
            >
              <li
                {...props}
                key={option.animationId}
                onClick={(e) => {
                  const target = e.target as HTMLElement; // Cast to HTMLElement
                  if (!target.closest(".icon-button")) {
                    AnimationDetails(option.animationId, option.systemNumber);
                  }
                }}
              >
                <Box
                  component="img"
                  className="Image"
                  src={option.bannerUrl}
                  alt={option.animationTagName}
                  onClick={() =>
                    AnimationDetails(option.animationId, option.systemNumber)
                  }
                  sx={{
                    width: 60,
                    height: 60,
                    objectFit: "cover",
                    borderRadius: "4px",
                    marginRight: "10px",
                  }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ fontSize: "16px" }}>
                    {t(option.animationTagName)}
                  </Typography>
                  <Typography variant="h6" sx={{ fontSize: "12px" }}>
                    {t(option.systemTagName)}
                  </Typography>
                </Box>
                {!selectAnim.some((aid) => aid === option.animationId) ? (
                  <IconButton
                    className="icon-button add_button"
                    edge="end"
                    onClick={(e) => {
                      e.stopPropagation(); // Stop event bubbling
                      handleClickLink(option.animationId);
                    }}
                    sx={{
                      backgroundColor: "#1976d2",
                      marginRight: "10px",
                      "&:hover": {
                        backgroundColor: "#be1e2d",
                      },
                    }}
                  >
                    <img
                      src={shareIcon || "/placeholder.svg"}
                      alt="share_animation"
                      width={24}
                      height={24}
                    />
                  </IconButton>
                ) : (
                  <IconButton
                    className="icon-button add_button"
                    edge="end"
                    onClick={(e) => {
                      e.stopPropagation(); // Stop event bubbling
                      handleRemoveAnim(option.animationId);
                    }}
                    sx={{
                      backgroundColor: "transparent",
                      marginRight: "0px",
                    }}
                  >
                    <CancelRoundedIcon
                      sx={{
                        fontSize: "35px",
                        color: "#950000",
                      }}
                    />
                  </IconButton>
                )}
              </li>
            </Box>
          );
        }}
      />
      <LoginRestriction open={isRestrictionOpen} />
    </Box>
  );
};

export default SearchDropdown;
